import { Injectable } from '@angular/core';
import { formatAdminDate } from '@core/utils/admin-date-format';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  TemplateStats,
  Template,
  TemplateFilters,
  TemplatesResponse,
  TemplateApprovalRequest,
  TemplateApprovalResponse,
  TemplateDeleteResponse,
  TemplateDetailsResponse,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  CloneTemplateRequest,
  JsonValidationResponse,
  ApprovalQueueResponse,
  ApprovalStats,
  ApprovalActionRequest,
  TemplateAIConfig
} from '../models/template.models';

/**
 * Template Service
 * Handles all template management API calls for super admin
 */
@Injectable({
  providedIn: 'root'
})
export class TemplateService {
  private readonly apiUrl = `${environment.apiUrl}/superadmin/templates`;

  constructor(private http: HttpClient) {}

  // ==================== Read Operations ====================

  /**
   * Get template statistics
   */
  getTemplateStats(): Observable<TemplateStats> {
    return this.http.get<TemplateStats>(`${this.apiUrl}/stats`);
  }

  /**
   * Get templates with filters and pagination
   */
  getTemplates(
    filters: TemplateFilters,
    page: number = 1,
    pageSize: number = 20
  ): Observable<TemplatesResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('page_size', pageSize.toString());

    if (filters.search) {
      params = params.set('search', filters.search);
    }
    if (filters.status) {
      params = params.set('status', filters.status);
    }
    if (filters.category) {
      params = params.set('category', filters.category);
    }
    if (filters.visibility) {
      params = params.set('visibility', filters.visibility);
    }

    return this.http.get<TemplatesResponse>(this.apiUrl, { params });
  }

  /**
   * Get template by ID (returns TemplateDetailsResponse with versions)
   */
  getTemplate(templateId: string): Observable<TemplateDetailsResponse> {
    return this.http.get<TemplateDetailsResponse>(`${this.apiUrl}/${templateId}`);
  }

  // ==================== CRUD Operations ====================

  /**
   * Create a new template
   */
  createTemplate(request: CreateTemplateRequest): Observable<Template> {
    return this.http.post<Template>(this.apiUrl, request);
  }

  /**
   * Update an existing template
   */
  updateTemplate(templateId: string, request: UpdateTemplateRequest): Observable<Template> {
    return this.http.put<Template>(`${this.apiUrl}/${templateId}`, request);
  }

  /**
   * Delete template (soft delete by default)
   */
  deleteTemplate(templateId: string, hardDelete: boolean = false): Observable<TemplateDeleteResponse> {
    let params = new HttpParams();
    if (hardDelete) {
      params = params.set('hard_delete', 'true');
    }
    return this.http.delete<TemplateDeleteResponse>(`${this.apiUrl}/${templateId}`, { params });
  }

  /**
   * Clone a template
   */
  cloneTemplate(templateId: string, request: CloneTemplateRequest): Observable<Template> {
    return this.http.post<Template>(`${this.apiUrl}/${templateId}/clone`, request);
  }

  // ==================== Approval Operations ====================

  private readonly approvalApiUrl = `${environment.apiUrl}/super-admin/template-approvals`;

  /**
   * Approve or reject template (legacy)
   */
  approveTemplateLegacy(request: TemplateApprovalRequest): Observable<TemplateApprovalResponse> {
    return this.http.post<TemplateApprovalResponse>(
      `${this.apiUrl}/${request.template_id}/approval`,
      {
        action: request.action,
        reason: request.reason
      }
    );
  }

  /**
   * Approve a template by template ID with AI configuration
   * @param templateId The template ID
   * @param notes Optional notes for the approval
   * @param aiConfig Optional AI configuration for the template
   */
  approveTemplate(templateId: string, notes?: string, aiConfig?: TemplateAIConfig): Observable<any> {
    const body: ApprovalActionRequest = {
      notes: notes || 'Approved by admin'
    };
    if (aiConfig) {
      body.ai_config = aiConfig;
    }
    return this.http.post(`${this.apiUrl}/${templateId}/approval`, {
      action: 'approve',
      reason: notes || 'Approved by admin',
      ai_config: aiConfig
    });
  }

  /**
   * Reject a template by template ID
   * @param templateId The template ID
   * @param reason Rejection reason (required, min 10 chars)
   */
  rejectTemplate(templateId: string, reason: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/${templateId}/approval`, {
      action: 'reject',
      reason
    });
  }

  /**
   * Get approval statistics for dashboard
   */
  getApprovalStats(): Observable<ApprovalStats> {
    return this.http.get<ApprovalStats>(`${this.approvalApiUrl}/stats`);
  }

  /**
   * Get approval queue with filters and pagination
   */
  getApprovalQueue(
    page: number = 1,
    pageSize: number = 20,
    statusFilter?: string,
    documentType?: string,
    organizationId?: string
  ): Observable<ApprovalQueueResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('page_size', pageSize.toString());

    if (statusFilter) {
      params = params.set('status_filter', statusFilter);
    }
    if (documentType) {
      params = params.set('document_type', documentType);
    }
    if (organizationId) {
      params = params.set('organization_id', organizationId);
    }

    return this.http.get<ApprovalQueueResponse>(this.approvalApiUrl, { params });
  }

  /**
   * Approve a template (new approval queue API)
   * @param requestId The approval request ID
   * @param notes Optional notes for the approval
   * @param aiConfig Optional AI configuration for the template
   */
  approveRequest(requestId: string, notes?: string, aiConfig?: TemplateAIConfig): Observable<any> {
    const body: ApprovalActionRequest = {};
    if (notes) {
      body.notes = notes;
    }
    if (aiConfig) {
      body.ai_config = aiConfig;
    }
    return this.http.post(`${this.approvalApiUrl}/${requestId}/approve`, body);
  }

  /**
   * Reject a template (new approval queue API)
   */
  rejectRequest(requestId: string, reason: string): Observable<any> {
    return this.http.post(`${this.approvalApiUrl}/${requestId}/reject`, { reason });
  }

  // ==================== Validation ====================

  /**
   * Validate JSON template structure
   */
  validateJson(jsonContent: string): Observable<JsonValidationResponse> {
    return this.http.post<JsonValidationResponse>(`${this.apiUrl}/validate-json`, {
      json_content: jsonContent
    });
  }

  // ==================== Utility Methods ====================

  /**
   * Format number with commas
   */
  formatNumber(value: number | undefined | null): string {
    if (value === undefined || value === null) {
      return '0';
    }
    return value.toLocaleString('en-US');
  }

  /**
   * Format percentage
   */
  formatPercentage(value: number): string {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  }

  /**
   * Format date
   */
  formatDate(dateString: string | undefined | null): string {
    if (!dateString) {
      return 'N/A';
    }
    return formatAdminDate(dateString, 'date', 'N/A');
  }

  /**
   * Copy template ID to clipboard
   */
  async copyTemplateId(templateId: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(templateId);
      return true;
    } catch (err) {
      console.error('Failed to copy template ID:', err);
      return false;
    }
  }

  /**
   * Format template ID for display (truncated)
   */
  formatTemplateId(templateId: string, maxLength: number = 12): string {
    if (!templateId) return '';
    if (templateId.length <= maxLength) return templateId;
    return `${templateId.substring(0, maxLength)}...`;
  }
}

